import { inject, Injectable } from '@angular/core';
import { AlwaysOnMode, GPS, GpsPulse, SdCycle } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { RemoteControlResponse } from '@hunting-nature/shared-core/types/remote-control-response.type';
import { normalizeError } from '@hunting-nature/shared-core/utils/normalize-error';
import { NavController } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, exhaustMap, filter, forkJoin, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { RemoteControlResponseMapper } from '../../../shared/mappers/remote-controll-response.mapper';
import { IonNavService } from '../../../shared/services/ion-nav.service';
import { ShopService } from '../../../shared/services/shop.service';
import { ToastService } from '../../../shared/services/toast.service';
import { CamerasPageService } from '../services/cameras.page.service';
import { RemoteControlService } from '../services/remote-control.service';

import { CamerasActions } from './cameras.actions';
import { CameraSelectors } from './cameras.reducer';
import { RemoteControlActions } from './remote-control.actions';
import { RemoteControlSelectors } from './remote-control.reducer';

@Injectable()
export class RemoteControlEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly navController = inject(NavController);
  private readonly remoteControlService = inject(RemoteControlService);
  private readonly toast = inject(ToastService);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);
  private readonly camerasService = inject(CamerasPageService);
  private readonly ionNavService = inject(IonNavService);
  private readonly shopService = inject(ShopService);
  private readonly transloco = inject(TranslocoService);

  private showErrorToast(translationKey: string): void {
    const message = this.transloco.translate(translationKey);
    this.toast.showBadRequestToast(message);
  }

  enterRemoteControlPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterRemoteControlPage),
      mergeMap(action =>
        forkJoin({
          remoteControl$: this.remoteControlService.fetchRemoteControlStatus$(action.imei),
          subscription$: this.remoteControlService.fetchSubscriptionDetails$(),
          camera$: action.cameraId ? this.camerasService.getCameraById$(action.cameraId) : of(undefined),
        }).pipe(
          map(responses =>
            RemoteControlActions.enterRemoteControlPageSucceeded({
              remoteControl: responses.remoteControl$,
              subscription: responses.subscription$,
              camera: responses.camera$,
              videoSettings: RemoteControlResponseMapper.toVideoSettings(responses.remoteControl$),
            })
          ),
          catchError((error: unknown) => of(RemoteControlActions.enterRemoteControlPageFailed({ error: normalizeError(error) })))
        )
      )
    )
  );
  enterRemoteControlPageFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.enterRemoteControlPageFailed),
        tap(() => this.showErrorToast('errors.remoteControl.enterRemoteControlPageFailed'))
      ),
    { dispatch: false }
  );

  fetchRemoteControlStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.fetchRemoteControlStatus),
      mergeMap(action =>
        forkJoin([this.remoteControlService.fetchRemoteControlStatus$(action.imei), this.remoteControlService.fetchSubscriptionDetails$()]).pipe(
          map(([remoteControl, subscription]) => RemoteControlActions.fetchRemoteControlStatusSucceeded({ data: remoteControl, subscription })),
          catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyImageSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyPictureNumberToSendPending),
      exhaustMap(action =>
        this.remoteControlService.applyImageSelection$(action.identCode, { picNumberToSend: action.pictureNumberToSend.toString() }).pipe(
          map(() => RemoteControlActions.applyPictureNumberToSendPendingSucceeded({ pictureNumberToSend: action.pictureNumberToSend })),
          catchError((error: unknown) => of(RemoteControlActions.applyPictureNumberToSendPendingFailed({ error: error as Error })))
        )
      )
    )
  );

  enterFooterCamNamePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterFooterCamNamePage),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      switchMap(([action, imei]) =>
        imei
          ? of(RemoteControlActions.enterFooterCamNamePageSucceeded({ data: undefined }))
          : this.remoteControlService.fetchRemoteControlStatus$(action.imei).pipe(
              map(data => RemoteControlActions.enterFooterCamNamePageSucceeded({ data })),
              catchError((error: unknown) => of(RemoteControlActions.enterFooterCamNamePageFailed({ error: normalizeError(error) })))
            )
      )
    )
  );

  cancelFooterCamName$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.cancelFooterCamName),
        tap(_ => {
          this.navController.back();
        })
      ),
    { dispatch: false }
  );
  applyFooterCamName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyFooterCamName),
      concatMap(action =>
        this.remoteControlService
          .applyFooterCamName$(action.identCode, {
            camNameString: action.footerCamName,
          })
          .pipe(
            map(() => RemoteControlActions.applyFooterCamNameSucceeded({ footerCamName: action.footerCamName })),
            catchError((error: unknown) => of(RemoteControlActions.applyFooterCamNameFailed({ error: normalizeError(error) })))
          )
      )
    )
  );
  applyFooterCamNameSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyFooterCamNameSucceeded),
        tap(_ => {
          // Navigation removed - user stays on page after saving (for new inline UI)
          // Previously: this.navController.back();
        })
      ),
    { dispatch: false }
  );
  applyFooterCamNameFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyFooterCamNameFailed),
        tap(() => this.showErrorToast('errors.remoteControl.footerCamNameFailed'))
      ),
    { dispatch: false }
  );

  enterPauseCameraPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterPauseCameraPage),
      mergeMap(action =>
        forkJoin([this.remoteControlService.fetchRemoteControlStatus$(action.imei), this.remoteControlService.fetchSubscriptionDetails$()]).pipe(
          map(([remoteControl, subscription]) => RemoteControlActions.fetchRemoteControlStatusSucceeded({ data: remoteControl, subscription })),
          catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  // Inline save effects for individual system settings
  saveGpsOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.setLocalGps),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      filter(([action, imei]) => !!action.cameraIdentCode && !!imei),
      switchMap(([action, imei]) =>
        this.remoteControlService.applyGps$(action.cameraIdentCode, { gps: action.enabled ? GPS.ON : GPS.OFF }).pipe(
          switchMap(() => this.remoteControlService.fetchRemoteControlStatus$(imei!)),
          map((state: RemoteControlResponse) => RemoteControlActions.saveGpsSucceeded({ state })),
          catchError((error: unknown) => of(RemoteControlActions.saveGpsFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  saveGpsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.saveGpsFailed),
        tap(() => this.showErrorToast('errors.remoteControl.systemSettingsFailed'))
      ),
    { dispatch: false }
  );

  saveAlwaysOnOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.setLocalAlwaysOn),
      filter(action => !!action.cameraIdentCode && !!action.imei),
      switchMap(action =>
        this.remoteControlService.applyInstantMode$(action.cameraIdentCode, { instantMode: action.enabled ? AlwaysOnMode.On : AlwaysOnMode.Off }).pipe(
          switchMap(() => this.remoteControlService.fetchRemoteControlStatus$(action.imei)),
          map((state: RemoteControlResponse) => RemoteControlActions.saveAlwaysOnSucceeded({ state })),
          catchError((error: unknown) => of(RemoteControlActions.saveAlwaysOnFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  saveAlwaysOnFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.saveAlwaysOnFailed),
        tap(() => this.showErrorToast('errors.remoteControl.systemSettingsFailed'))
      ),
    { dispatch: false }
  );

  saveSecaTrackOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.setLocalSecaTrack),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      filter(([action, imei]) => !!action.cameraIdentCode && !!imei),
      switchMap(([action, imei]) =>
        this.remoteControlService.applyGpsPulse$(action.cameraIdentCode, { camControlJob: { gpsPulse: action.enabled ? GpsPulse.ON : GpsPulse.OFF } }).pipe(
          switchMap(() => this.remoteControlService.fetchRemoteControlStatus$(imei!)),
          map((state: RemoteControlResponse) => RemoteControlActions.saveSecaTrackSucceeded({ state })),
          catchError((error: unknown) => of(RemoteControlActions.saveSecaTrackFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  saveSecaTrackFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.saveSecaTrackFailed),
        tap(() => this.showErrorToast('errors.remoteControl.systemSettingsFailed'))
      ),
    { dispatch: false }
  );

  saveSdCycleOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.setLocalSdCycle),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      filter(([action, imei]) => !!action.cameraIdentCode && !!imei),
      switchMap(([action, imei]) =>
        this.remoteControlService.applySdCycle$(action.cameraIdentCode, action.enabled ? SdCycle.ON : SdCycle.OFF).pipe(
          switchMap(() => this.remoteControlService.fetchRemoteControlStatus$(imei!)),
          map((state: RemoteControlResponse) => RemoteControlActions.saveSdCycleSucceeded({ state })),
          catchError((error: unknown) => of(RemoteControlActions.saveSdCycleFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  saveSdCycleFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.saveSdCycleFailed),
        tap(() => this.showErrorToast('errors.remoteControl.systemSettingsFailed'))
      ),
    { dispatch: false }
  );

  saveFooterCamName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.saveFooterCamName),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      filter(([_, imei]) => !!imei),
      switchMap(([action, imei]) =>
        this.remoteControlService.applyFooterCamName$(action.cameraIdentCode, { camNameString: action.footerCamName }).pipe(
          switchMap(() => this.remoteControlService.fetchRemoteControlStatus$(imei!)),
          map((state: RemoteControlResponse) => RemoteControlActions.saveFooterCamNameSucceeded({ state })),
          catchError((error: unknown) => of(RemoteControlActions.saveFooterCamNameFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  saveFooterCamNameFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.saveFooterCamNameFailed),
        tap(() => this.showErrorToast('errors.remoteControl.systemSettingsFailed'))
      ),
    { dispatch: false }
  );

  applyPhotoSizeSdCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyImageSizeCard),
      switchMap(action =>
        this.remoteControlService.applyImageSize$(action.identCode, { imageSize: action.photoSize }).pipe(
          map(() => RemoteControlActions.applyImageSizeSucceeded({ photoSize: action.photoSize })),
          catchError((error: unknown) => of(RemoteControlActions.applyImageSizeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  enterSecaTrackPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterSecaTrackPage),
      withLatestFrom(this.store.select(RemoteControlSelectors.imei)),
      switchMap(([action, imei]) =>
        imei
          ? of(RemoteControlActions.enterSecaTrackPageSucceeded({ data: undefined }))
          : this.remoteControlService.fetchRemoteControlStatus$(action.imei).pipe(
              map(data => RemoteControlActions.enterSecaTrackPageSucceeded({ data })),
              catchError((error: unknown) => of(RemoteControlActions.enterSecaTrackPageFailed({ error: normalizeError(error) })))
            )
      )
    )
  );
  cancelSecaTrackPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.cancelSecaTrackPage),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );
  applyGpsPulse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyGpsPulse),
      concatMap(action =>
        this.remoteControlService.applyGpsPulse$(action.identCode, { camControlJob: { gpsPulse: action.gpsPulse } }).pipe(
          map(() => RemoteControlActions.applyGpsPulseSucceeded({ gpsPulse: action.gpsPulse })),
          catchError((error: unknown) => of(RemoteControlActions.applyGpsPulseFailed({ error: normalizeError(error) })))
        )
      )
    )
  );
  applyGpsPulseSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyGpsPulseSucceeded),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );
  applyGpsPulseFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyGpsPulseFailed),
        tap(() => this.showErrorToast('errors.remoteControl.gpsPulseFailed'))
      ),
    { dispatch: false }
  );

  applyGpsCoordinates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyGpsCoordinates),
      exhaustMap(action =>
        this.remoteControlService.applyGps$(action.identCode, { gps: action.gps }).pipe(
          map(() => RemoteControlActions.applyGpsCoordinatesSucceeded({ gpsCoordinates: action.gps })),
          catchError((error: unknown) => of(RemoteControlActions.applyGpsCoordinatesFailed({ error: error as Error })))
        )
      )
    )
  );
  applyGpsCoordinatesSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyGpsCoordinatesSucceeded),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );
  applyGpsCoordinatesFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyGpsCoordinatesFailed),
        tap(() => this.showErrorToast('errors.remoteControl.gpsCoordinatesFailed'))
      ),
    { dispatch: false }
  );

  updateFlashMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updateFlashMode),
      switchMap(action =>
        this.remoteControlService.applyNightMode$(action.cameraIdentCode, { nightMode: action.flashMode }).pipe(
          map(() => RemoteControlActions.updateFlashModeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.updateFlashModeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updateFlashModeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateFlashModeFailed),
        tap(() => this.showErrorToast('errors.remoteControl.flashModeFailed'))
      ),
    { dispatch: false }
  );

  updateMaxPhotos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updateMaxPhotos),
      switchMap(action =>
        this.remoteControlService.applyMaximalSentShotsPerHour$(action.cameraIdentCode, { maxNumPerHour: action.maxPhotos }).pipe(
          map(() => RemoteControlActions.updateMaxPhotosSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.updateMaxPhotosFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updateMaxPhotosFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateMaxPhotosFailed),
        tap(() => this.showErrorToast('errors.remoteControl.maxPhotosFailed'))
      ),
    { dispatch: false }
  );

  updateActiveMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updateActiveMonitoring),
      switchMap(action =>
        this.remoteControlService.applyTimerSetting$(action.cameraIdentCode, { timerSetting: action.timerSetting }).pipe(
          map(() => RemoteControlActions.updateActiveMonitoringSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.updateActiveMonitoringFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updateActiveMonitoringFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateActiveMonitoringFailed),
        tap(() => this.showErrorToast('errors.remoteControl.activeMonitoringFailed'))
      ),
    { dispatch: false }
  );

  // capture settings
  updateSensitivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updateSensitivity),
      switchMap(action =>
        this.remoteControlService.applySensitivity$(action.identCode, { sensitivity: action.sensitivity }).pipe(
          map(() => RemoteControlActions.updateSensitivitySucceeded({ sensitivity: action.sensitivity })),
          catchError((error: unknown) => of(RemoteControlActions.updateSensitivityFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updateSensitivityFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateSensitivityFailed),
        tap(() => this.showErrorToast('errors.remoteControl.sensitivityFailed'))
      ),
    { dispatch: false }
  );

  activatePauseCapture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.activatePauseCapture),
      exhaustMap(action =>
        this.remoteControlService.applyDelaySetting$(action.cameraIdentCode, action.imei, true, action.duration).pipe(
          map(() => RemoteControlActions.activatePauseCaptureSucceeded({ duration: action.duration })),
          catchError((error: unknown) => of(RemoteControlActions.activatePauseCaptureFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  deactivatePauseCapture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.deactivatePauseCapture),
      exhaustMap(action =>
        this.remoteControlService.applyDelaySetting$(action.cameraIdentCode, action.imei, false).pipe(
          map(() => RemoteControlActions.deactivatePauseCaptureSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.deactivatePauseCaptureFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updatePauseCapture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updatePauseCapture),
      exhaustMap(data =>
        this.remoteControlService.applyDelaySetting$(data.cameraIdentCode, data.imei, true, data.duration).pipe(
          map(() => RemoteControlActions.updatePauseCaptureSucceeded({ duration: data.duration })),
          catchError((error: unknown) => of(RemoteControlActions.updatePauseCaptureFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updatePauseCaptureFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updatePauseCaptureFailed),
        tap(() => this.showErrorToast('errors.remoteControl.pauseCaptureFailed'))
      ),
    { dispatch: false }
  );

  enterAlwaysOnPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterAlwaysOnPage),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails)),
      switchMap(([action, cameraDetails]) => {
        // TODO: move out here and embedd in redux flow
        if (cameraDetails?.id !== action.cameraId) {
          this.store.dispatch(CamerasActions.showCameraDetails({ cameraId: action.cameraId, imei: action.imei }));

          return this.actions$.pipe(
            ofType(CamerasActions.showCameraDetailsSucceeded),
            filter(successAction => successAction.camera.id === action.cameraId && !!successAction.camera.imei),
            map(successAction => RemoteControlActions.fetchRemoteControlStatus({ imei: successAction.camera.imei }))
          );
        }
        if (cameraDetails.imei) {
          return of(RemoteControlActions.fetchRemoteControlStatus({ imei: cameraDetails.imei }));
        }
        return of();
      })
    )
  );

  confirmAlwaysOn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.confirmAlwaysOn),
      map(action => RemoteControlActions.updateAlwaysOn({ cameraId: action.cameraId, alwaysOn: AlwaysOnMode.On }))
    )
  );

  updateAlwaysOn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.updateAlwaysOn),
      withLatestFrom(this.store.select(CameraSelectors.getCameraIdentCode)),
      exhaustMap(([action, cameraIdentCode]) => {
        if (!cameraIdentCode) {
          return of(RemoteControlActions.updateAlwaysOnFailed({ error: new Error('Camera identification code not found') }));
        }

        return this.remoteControlService.applyInstantMode$(cameraIdentCode, { instantMode: action.alwaysOn }).pipe(
          switchMap(() => [RemoteControlActions.updateAlwaysOnSucceeded()]),
          catchError((error: unknown) => {
            return of(RemoteControlActions.updateAlwaysOnFailed({ error: normalizeError(error) }));
          })
        );
      })
    )
  );

  updateAlwaysOnFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateAlwaysOnFailed),
        tap(() => {
          this.store.dispatch(RemoteControlActions.hideAlwaysOnConfirmationAlert());
          this.showErrorToast('errors.remoteControl.alwaysOnFailed');
        })
      ),
    { dispatch: false }
  );

  updateAlwaysOnSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.updateAlwaysOnSucceeded),
        tap(() => {
          this.store.dispatch(RemoteControlActions.hideAlwaysOnConfirmationAlert());
        }),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );

  activateQuickPhotoMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.activateQuickPhotoMode),
      switchMap(action =>
        this.remoteControlService.applySnapshotNumber$(action.cameraIdentCode, action.snapshotNumber, true).pipe(
          map(_data => RemoteControlActions.activateQuickPhotoModeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.activateQuickPhotoModeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  deactivateQuickPhotoMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.deactivateQuickPhotoMode),
      switchMap(action =>
        this.remoteControlService.applySnapshotNumber$(action.cameraIdentCode, action.snapshotNumber, false).pipe(
          map(_data => RemoteControlActions.deactivateQuickPhotoModeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.deactivateQuickPhotoModeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applySnapshotNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applySnapshotNumber),
      switchMap(action =>
        forkJoin([
          action.snapshotNumber < action.pictureNumberToSend
            ? this.remoteControlService.applyImageSelection$(action.cameraIdentCode, {
                picNumberToSend: action.snapshotNumber.toString(),
              })
            : of(action.snapshotNumber.toString()),
          this.remoteControlService.applySnapshotNumber$(action.cameraIdentCode, action.snapshotNumber, action.isQuickMode),
        ]).pipe(
          map(() => RemoteControlActions.applySnapshotNumberSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applySnapshotNumberFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applySendingPictureSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applySendingPictureSize),
      switchMap(action =>
        this.remoteControlService.applySendingPicSize$(action.cameraIdentCode, { sendingPicSize: action.photoSize }).pipe(
          map(() => RemoteControlActions.applySendingPictureSizeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applySendingPictureSizeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyCameraResetFactorySettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyCameraResetFactorySettings),
      exhaustMap(action =>
        this.remoteControlService.applyCameraResetFactorySettings$(action.cameraIdentCode).pipe(
          map(() => RemoteControlActions.applyCameraResetFactorySettingsSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applyCameraResetFactorySettingsFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyCameraResetFactorySettingsSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyCameraResetFactorySettingsSucceeded),
        tap(() => {
          // Nothing to do here
        })
      ),
    { dispatch: false }
  );

  applyCameraResetFactorySettingsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyCameraResetFactorySettingsFailed),
        tap(action => this.toastService.showTranslatedError('errors.cameras.applyCameraResetFactorySettingsFailed', { errorMessage: action.error.message }))
      ),
    { dispatch: false }
  );

  enableVideoRecording$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enableVideoRecording),
      exhaustMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.enableVideoRecordingSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.enableVideoRecordingFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  disableVideoRecording$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.disableVideoRecording),
      exhaustMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.disableVideoRecordingSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.disableVideoRecordingFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  enableVideoTransmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enableVideoTransmission),
      withLatestFrom(this.store.select(RemoteControlSelectors.getVideoLength)),
      exhaustMap(([action, videoLength]) =>
        this.remoteControlService
          .applyVideoSettings$(action.cameraIdentCode, {
            ...action.settings,
            videoLength,
          })
          .pipe(
            map(() => RemoteControlActions.enableVideoTransmissionSucceeded()),
            catchError((error: unknown) => of(RemoteControlActions.enableVideoTransmissionFailed({ error: normalizeError(error) })))
          )
      )
    )
  );

  disableVideoTransmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.disableVideoTransmission),
      exhaustMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.disableVideoTransmissionSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.disableVideoTransmissionFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  changeVideoSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.changeVideoSize),
      switchMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.changeVideoSizeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.enableVideoRecordingFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  changeVideoLength$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.changeVideoLength),
      switchMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.changeVideoLengthSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.changeVideoLengthFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  changeVideoSettingFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RemoteControlActions.changeVideoSizeFailed,
          RemoteControlActions.changeVideoLengthFailed,
          RemoteControlActions.enableVideoRecordingFailed,
          RemoteControlActions.disableVideoRecordingFailed,
          RemoteControlActions.enableVideoTransmissionFailed,
          RemoteControlActions.disableVideoTransmissionFailed,
          RemoteControlActions.changeVideoTransmissionTypeFailed
        ),
        tap(() => this.showErrorToast('errors.remoteControl.videoSettingsFailed'))
      ),
    { dispatch: false }
  );

  changeVideoTransmissionType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.changeVideoTransmissionType),
      switchMap(action =>
        this.remoteControlService.applyVideoSettings$(action.cameraIdentCode, action.settings).pipe(
          map(() => RemoteControlActions.changeVideoTransmissionTypeSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.changeVideoTransmissionTypeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  goBackToRemoteControlFromVideoSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.goBackToRemoteControlFromVideoSettings),
        switchMap(() => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );

  applyTimeLapseSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyTimeLapseSettings),
      switchMap(action =>
        this.remoteControlService
          .applyTimelapseSetting$(action.cameraIdentCode, {
            timelapseSetting: {
              timelapseSetting: action.timelapseSetting,
              PIR: action.motionSensorSetting,
            },
          })
          .pipe(
            map(() => RemoteControlActions.applyTimeLapseSettingsSucceeded()),
            catchError((error: unknown) => of(RemoteControlActions.applyTimeLapseSettingsFailed({ error: normalizeError(error) })))
          )
      )
    )
  );

  applyTimeLapseSettingsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyTimeLapseSettingsFailed),
        tap(() => this.showErrorToast('errors.remoteControl.timeLapseFailed'))
      ),
    { dispatch: false }
  );

  applyControlPhotos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyControlPhotos),
      switchMap(action =>
        this.remoteControlService.applyControlPhotos$(action.cameraIdentCode, action.timer1, action.timer2).pipe(
          map(() => RemoteControlActions.applyControlPhotosSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applyControlPhotosFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyControlPhotosFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyControlPhotosFailed),
        tap(() => this.showErrorToast('errors.remoteControl.timeLapseFailed'))
      ),
    { dispatch: false }
  );

  enterSdCyclePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterSdCyclePage),
      switchMap(action =>
        this.camerasService.getCameraById$(action.cameraId).pipe(
          switchMap(camera => {
            if (!camera.imei) {
              return of(
                RemoteControlActions.fetchRemoteControlStatusFailed({
                  error: new Error(`Camera ${action.cameraId} has no IMEI`),
                })
              );
            }
            return this.remoteControlService.fetchRemoteControlStatus$(camera.imei).pipe(
              map(data => RemoteControlActions.fetchRemoteControlStatusSucceeded({ data })),
              catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
            );
          }),
          catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applySdCycle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applySdCycle),
      switchMap(action =>
        this.remoteControlService.applySdCycle$(action.cameraIdentCode, action.selectedSdCycle).pipe(
          map(() => RemoteControlActions.applySdCycleSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applySdCycleFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applySdCycleSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applySdCycleSucceeded),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );

  applySdCycleFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applySdCycleFailed),
        tap(() => this.showErrorToast('errors.remoteControl.sdCycleFailed'))
      ),
    { dispatch: false }
  );

  cancelSdCycle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.cancelSdCycle),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );

  enterResetPinPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.enterResetPinPage),
      switchMap(action =>
        this.camerasService.getCameraById$(action.cameraId).pipe(
          switchMap(camera => {
            if (!camera.imei) {
              return of(
                RemoteControlActions.fetchRemoteControlStatusFailed({
                  error: new Error(`Camera ${action.cameraId} has no IMEI`),
                })
              );
            }
            return this.remoteControlService.fetchRemoteControlStatus$(camera.imei).pipe(
              map(data => RemoteControlActions.fetchRemoteControlStatusSucceeded({ data })),
              catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
            );
          }),
          catchError((error: unknown) => of(RemoteControlActions.fetchRemoteControlStatusFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyResetPin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyResetPin),
      switchMap(action =>
        this.remoteControlService.applyPinCode$(action.cameraIdentCode).pipe(
          map(() => RemoteControlActions.applyResetPinSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applyResetPinFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyResetPinSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyResetPinSucceeded),
        tap(() => {
          // Nothing to do here
        })
      ),
    { dispatch: false }
  );

  applyResetPinFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyResetPinFailed),
        tap(action => this.toastService.showTranslatedError('errors.cameras.applyResetPinFailed', { errorMessage: action.error.message }))
      ),
    { dispatch: false }
  );

  applyStatusReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteControlActions.applyStatusReport),
      exhaustMap(action =>
        this.remoteControlService.applyStatusReport$(action.cameraIdentCode, action.interval).pipe(
          map(() => RemoteControlActions.applyStatusReportSucceeded()),
          catchError((error: unknown) => of(RemoteControlActions.applyStatusReportFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  applyStatusReportFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.applyStatusReportFailed),
        tap(() => this.showErrorToast('errors.remoteControl.statusReportFailed'))
      ),
    { dispatch: false }
  );

  getServicePlan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.getServicePlan),
        switchMap(_action => this.shopService.openShop())
      ),
    { dispatch: false }
  );

  upgradeServicePlan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteControlActions.upgradeServicePlan),
        switchMap(_action => this.shopService.openSubscriptionManager())
      ),
    { dispatch: false }
  );
}
